/**
 * @file ClientContacts.js
 * @description This file contains the ClientContacts component, which is a reusable form for displaying and editing an individual's demographic information.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    updateClientContact,
    deleteClientContact,
} from '../../services/clientService';
import Toast from '../ui/toasts/Toast';
import { formatPhoneNumber } from '../../utils/utils';
import {
    FilterModal,
    useFilter,
    renderFilterIcon,
} from '../helpers/FilterComponent';
import styles from './ClientList.module.scss';
import '../page-list/PageList.css';
import { getText } from '../../i18n';

const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const ClientContactsTab = ({
    individualId,
    individualRecord,
    onSave,
    onCancel,
    populationGroups = [],
    initialData = {},
    isCreateMode = false,
}) => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [contacts, setContacts] = useState(individualRecord.contacts);
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [sortConfig, setSortConfig] = useState({
        key: 'fullName',
        direction: SortDirections.ASCENDING,
    });
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});
    const [baseFilteredData, setBaseFilteredData] = useState([]);
    const [filteredData, applyFilter, clearAllFilters] =
        useFilter(baseFilteredData);

    const selectAllRef = useRef(null);

    const enrichedContacts = useMemo(() => {
        return contacts.map(contact => ({
            ...contact,
            fullName:
                `${contact.firstName || ''} ${contact.lastName || ''}`.trim(),
        }));
    }, [contacts]);

    useEffect(() => {
        setBaseFilteredData(enrichedContacts);
    }, [enrichedContacts]);

    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate =
                selectedContacts.length > 0 &&
                selectedContacts.length < filteredData.length;
        }
    }, [selectedContacts, filteredData]);

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
        applyFilter(column, selectedOptions);
    };

    const clearFilters = () => {
        setSelections({});
        clearAllFilters();
        setBaseFilteredData(enrichedContacts);
    };

    const getOptions = column => {
        const filtersWithoutCurrent = { ...selections };
        delete filtersWithoutCurrent[column];

        const filteredContacts = enrichedContacts.filter(contact =>
            Object.entries(filtersWithoutCurrent).every(
                ([key, values]) =>
                    values.length === 0 || values.includes(contact[key]),
            ),
        );

        return Array.from(new Set(filteredContacts.map(item => item[column])))
            .filter(Boolean)
            .sort((a, b) => {
                if (typeof a === 'string' && typeof b === 'string') {
                    return a.localeCompare(b);
                }
                return a - b;
            });
    };

    const sortedContacts = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            if (sortConfig.key === 'fullName') {
                const lastNameComparison = (a.lastName || '')
                    .toLowerCase()
                    .localeCompare((b.lastName || '').toLowerCase());

                if (lastNameComparison !== 0) {
                    return sortConfig.direction === SortDirections.ASCENDING
                        ? lastNameComparison
                        : -lastNameComparison;
                }

                const firstNameComparison = (a.firstName || '')
                    .toLowerCase()
                    .localeCompare((b.firstName || '').toLowerCase());

                return sortConfig.direction === SortDirections.ASCENDING
                    ? firstNameComparison
                    : -firstNameComparison;
            }

            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (!aValue) aValue = '';
            if (!bValue) bValue = '';

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (aValue < bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? -1
                    : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? 1
                    : -1;
            }
            return 0;
        });
    }, [filteredData, sortConfig]);

    const handleSort = key => {
        setSortConfig(prevConfig => ({
            key,
            direction:
                prevConfig.key === key &&
                prevConfig.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING,
        }));
    };

    const handleSortName = () => handleSort('fullName');
    const handleSortMain = () => handleSort('isMain');
    const handleSortPhone = () => handleSort('phoneNumber');
    const handleSortEmail = () => handleSort('emailAddress');
    const handleSortWebsite = () => handleSort('website');
    const handleSortContactType = () => handleSort('contactType');
    const handleSortRole = () => handleSort('contactRole');

    const handleMakeContactMain = contactId => {
        const updatedContacts = [...contacts];

        const updatedContactToNoMain = updatedContacts.find(item => {
            if (item.isMain === true) {
                item.isMain = false;
                return true;
            }
            return false;
        });

        if (updatedContactToNoMain) {
            updateClientContact(updatedContactToNoMain, individualId).then(
                () => {},
            );
        }

        const updatedContactToMain = updatedContacts.find(item => {
            if (item.id === contactId) {
                item.isMain = true;
                return true;
            }
            return false;
        });

        updateClientContact(updatedContactToMain, individualId)
            .then(() => {
                Toast.success(getText('msg_0dc8657aa9a3'));
            })
            .catch(error => {
                Toast.error(
                    getText('msg_bcb2c28973f3'),
                );
            });

        setContacts([...updatedContacts]);
    };

    const handleContactSelection = contactId => {
        if (selectedContacts.includes(contactId)) {
            setSelectedContacts(
                selectedContacts.filter(id => id !== contactId),
            );
        } else {
            setSelectedContacts([...selectedContacts, contactId]);
        }
    };

    const handleSelectAll = event => {
        if (event.target.checked) {
            setSelectedContacts(filteredData.map(contact => contact.id));
        } else {
            setSelectedContacts([]);
        }
    };

    const handleDeleteContacts = () => {
        const successfullyDeletedContacts = [];

        selectedContacts.forEach(contactId => {
            const contactToDelete = contacts.find(
                contact => contact.id === contactId,
            );
            if (contactToDelete && !contactToDelete.isMain) {
                deleteClientContact(contactId, individualId)
                    .then(() => {
                        setContacts(
                            contacts.filter(
                                contact => contact.id !== contactId,
                            ),
                        );
                        successfullyDeletedContacts.push(contactId);
                    })
                    .catch(error => {
                        Toast.error(
                            getText('msg_2414171cacf9'),
                        );
                    });
            } else if (contactToDelete && contactToDelete.isMain) {
                Toast.warning(
                    getText('msg_7ecac9958aa8'),
                );
            }
        });

        setSelectedContacts(
            selectedContacts.filter(
                contactId => !successfullyDeletedContacts.includes(contactId),
            ),
        );
    };

    const handleNavigateToUpdateContact = (contactId, contactData) => {
        navigate(`update-contact/${contactId}`, {
            state: {
                contactData,
                clientId: individualId,
                existingTab: 'Contacts',
            },
        });
    };

    return (
        <div className="card-body px-0 py-4">
            {/* Screen Header with Title and Actions */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                {/* Screen Title */}
                <p className="page-title float-start card-title ms-4"></p>
                {/* Screen Actions */}
                <div className="float-end d-flex align-items-center">
                    <span
                        type="button"
                        onClick={clearFilters}
                        className="ing-text-buttons mx-3"
                    >
                        <i className="bi bi-arrow-counterclockwise me-2" />
                        {getText('msg_381c4bf1d409')}
                    </span>
                    <span
                        type="button"
                        onClick={handleDeleteContacts}
                        className="ing-text-buttons mx-3"
                    >
                        <i className="bi bi-trash3 me-2" />
                        {getText('msg_f6fdbe48dc54')}
                    </span>
                    <span
                        onClick={() => navigate('add-contact')}
                        type="button"
                        className="ms-3 me-4  button-base ing-button-primary-left align-middle"
                    >
                        <i className="bi bi-plus-lg me-1" />
                        {getText('msg_61cc55aa0453')}
                    </span>
                </div>
            </div>
            <div className="table-responsive-xxl w-100">
                {/* Template List Table */}
                <table className="table table-hover align-middle">
                    <thead className="custom-table-header table-light">
                        <tr>
                            <th className="text-center">
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    ref={selectAllRef}
                                    checked={
                                        selectedContacts.length ===
                                            filteredData.length &&
                                        filteredData.length > 0
                                    }
                                    onChange={handleSelectAll}
                                />
                            </th>
                            <th>
                                <span
                                    onClick={handleSortName}
                                    style={{ cursor: 'pointer' }}
                                    className=" "
                                >
                                    {getText('msg_709a23220f2c')}
                                    <svg
                                        width="10.5"
                                        height="6"
                                        viewBox="0 0 10.5 6"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="ms-2"
                                    >
                                        <path
                                            transform={
                                                sortConfig.key === 'fullName' &&
                                                sortConfig.direction ===
                                                    SortDirections.DESCENDING
                                                    ? 'rotate(180 5.25 3)'
                                                    : ''
                                            }
                                            d="M1 0.75L5.25 5.25L9.5 0.75"
                                            stroke="#2F2B43"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </span>
                                <span
                                    onClick={e => {
                                        e.stopPropagation();
                                        openModal('fullName');
                                    }}
                                    className={styles.cursorPointer}
                                >
                                    {renderFilterIcon(
                                        selections['fullName']?.length > 0,
                                    )}
                                </span>
                                {activeModal === 'fullName' && (
                                    <FilterModal
                                        column={activeModal}
                                        options={getOptions(activeModal)}
                                        selectedOptions={
                                            selections[activeModal] || []
                                        }
                                        onApply={handleApply}
                                        onClose={closeModal}
                                    />
                                )}
                            </th>
                            <th>
                                <span
                                    onClick={handleSortMain}
                                    style={{ cursor: 'pointer' }}
                                    className=" "
                                >
                                    {getText('msg_a9a96ec01949')}
                                    <svg
                                        width="10.5"
                                        height="6"
                                        viewBox="0 0 10.5 6"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="ms-2"
                                    >
                                        <path
                                            transform={
                                                sortConfig.key === 'isMain' &&
                                                sortConfig.direction ===
                                                    SortDirections.DESCENDING
                                                    ? 'rotate(180 5.25 3)'
                                                    : ''
                                            }
                                            d="M1 0.75L5.25 5.25L9.5 0.75"
                                            stroke="#2F2B43"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </span>
                            </th>
                            <th>
                                <span
                                    onClick={handleSortPhone}
                                    style={{ cursor: 'pointer' }}
                                    className=" "
                                >
                                    {getText('msg_77064d526523')}
                                    {/* <svg
                                        width="10.5"
                                        height="6"
                                        viewBox="0 0 10.5 6"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="ms-2"
                                    >
                                        <path
                                            transform={
                                                sortConfig.key ===
                                                    'phoneNumber' &&
                                                sortConfig.direction ===
                                                    SortDirections.DESCENDING
                                                    ? 'rotate(180 5.25 3)'
                                                    : ''
                                            }
                                            d="M1 0.75L5.25 5.25L9.5 0.75"
                                            stroke="#2F2B43"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg> */}
                                </span>
                                <span
                                    onClick={e => {
                                        e.stopPropagation();
                                        openModal('phoneNumber');
                                    }}
                                    className={styles.cursorPointer}
                                >
                                    {renderFilterIcon(
                                        selections['phoneNumber']?.length > 0,
                                    )}
                                </span>
                                {activeModal === 'phoneNumber' && (
                                    <FilterModal
                                        column={activeModal}
                                        options={getOptions(activeModal)}
                                        selectedOptions={
                                            selections[activeModal] || []
                                        }
                                        onApply={handleApply}
                                        onClose={closeModal}
                                    />
                                )}
                            </th>
                            <th>
                                <span
                                    onClick={handleSortEmail}
                                    style={{ cursor: 'pointer' }}
                                    className=" "
                                >
                                    {getText('msg_84add5b29527')}
                                    {/* <svg
                                        width="10.5"
                                        height="6"
                                        viewBox="0 0 10.5 6"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="ms-2"
                                    >
                                        <path
                                            transform={
                                                sortConfig.key ===
                                                    'emailAddress' &&
                                                sortConfig.direction ===
                                                    SortDirections.DESCENDING
                                                    ? 'rotate(180 5.25 3)'
                                                    : ''
                                            }
                                            d="M1 0.75L5.25 5.25L9.5 0.75"
                                            stroke="#2F2B43"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg> */}
                                </span>
                                <span
                                    onClick={e => {
                                        e.stopPropagation();
                                        openModal('emailAddress');
                                    }}
                                    className={styles.cursorPointer}
                                >
                                    {renderFilterIcon(
                                        selections['emailAddress']?.length > 0,
                                    )}
                                </span>
                                {activeModal === 'emailAddress' && (
                                    <FilterModal
                                        column={activeModal}
                                        options={getOptions(activeModal)}
                                        selectedOptions={
                                            selections[activeModal] || []
                                        }
                                        onApply={handleApply}
                                        onClose={closeModal}
                                    />
                                )}
                            </th>
                         
                            <th>
                                <span
                                    onClick={handleSortContactType}
                                    style={{ cursor: 'pointer' }}
                                    className=" "
                                >
                                    {getText('msg_fcbedcab55ea')}
                                    {/* <svg
                                        width="10.5"
                                        height="6"
                                        viewBox="0 0 10.5 6"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="ms-2"
                                    >
                                        <path
                                            transform={
                                                sortConfig.key ===
                                                    'contactType' &&
                                                sortConfig.direction ===
                                                    SortDirections.DESCENDING
                                                    ? 'rotate(180 5.25 3)'
                                                    : ''
                                            }
                                            d="M1 0.75L5.25 5.25L9.5 0.75"
                                            stroke="#2F2B43"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg> */}
                                </span>
                                <span
                                    onClick={e => {
                                        e.stopPropagation();
                                        openModal('contactType');
                                    }}
                                    className={styles.cursorPointer}
                                >
                                    {renderFilterIcon(
                                        selections['contactType']?.length > 0,
                                    )}
                                </span>
                                {activeModal === 'contactType' && (
                                    <FilterModal
                                        column={activeModal}
                                        options={getOptions(activeModal)}
                                        selectedOptions={
                                            selections[activeModal] || []
                                        }
                                        onApply={handleApply}
                                        onClose={closeModal}
                                    />
                                )}
                            </th>
                            <th>
                                <span
                                    onClick={handleSortRole}
                                    style={{ cursor: 'pointer' }}
                                    className=" "
                                >
                                    {getText('msg_c3f104d13657')}
                                    {/* <svg
                                        width="10.5"
                                        height="6"
                                        viewBox="0 0 10.5 6"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="ms-2"
                                    >
                                        <path
                                            transform={
                                                sortConfig.key ===
                                                    'contactRole' &&
                                                sortConfig.direction ===
                                                    SortDirections.DESCENDING
                                                    ? 'rotate(180 5.25 3)'
                                                    : ''
                                            }
                                            d="M1 0.75L5.25 5.25L9.5 0.75"
                                            stroke="#2F2B43"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg> */}
                                </span>
                                <span
                                    onClick={e => {
                                        e.stopPropagation();
                                        openModal('contactRole');
                                    }}
                                    className={styles.cursorPointer}
                                >
                                    {renderFilterIcon(
                                        selections['contactRole']?.length > 0,
                                    )}
                                </span>
                                {activeModal === 'contactRole' && (
                                    <FilterModal
                                        column={activeModal}
                                        options={getOptions(activeModal)}
                                        selectedOptions={
                                            selections[activeModal] || []
                                        }
                                        onApply={handleApply}
                                        onClose={closeModal}
                                    />
                                )}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedContacts.map((contact, index) => (
                            <tr
                                key={contact.id}
                                style={{
                                    '--bs-table-bg':
                                        index % 2 === 1 ? '#f8f9fa' : '',
                                }}
                            >
                                <td className="text-center">
                                    <input
                                        id={contact.id}
                                        checked={selectedContacts.includes(
                                            contact.id,
                                        )}
                                        onChange={() =>
                                            handleContactSelection(contact.id)
                                        }
                                        className="form-check-input"
                                        type="checkbox"
                                    />
                                </td>
                                <td>
                                    <span
                                        className="titleCell"
                                        onClick={() =>
                                            handleNavigateToUpdateContact(
                                                contact.id,
                                                contact,
                                            )
                                        }
                                    >
                                        {contact.fullName}
                                    </span>
                                </td>
                                <td
                                    className="subtitleCell"
                                    style={{ paddingLeft: '32px' }}
                                >
                                    {contact.isMain ? (
                                        'Y'
                                    ) : (
                                        <span
                                            style={{ cursor: 'pointer' }}
                                            onClick={() =>
                                                handleMakeContactMain(
                                                    contact.id,
                                                )
                                            }
                                        >
                                            -
                                        </span>
                                    )}
                                </td>
                                <td className="subtitleCell" style={{ paddingLeft: '32px' }}>
                                    {contact.phoneNumber
                                        ? formatPhoneNumber(
                                              contact.phoneNumber,
                                          )
                                        : '-'}
                                </td>
                                <td className="subtitleCell">
                                    {contact.emailAddress}
                                </td>
                                
                                <td className="subtitleCell">
                                    {contact.contactType}
                                </td>
                                <td className="subtitleCell">
                                    {contact.contactRole}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p
                className="text-center fs-5"
                style={{ margin: '60px 0', color: 'rgba(47, 43, 67, 0.6)' }}
            >
                {sortedContacts.length <= 0 ? 'No contacts found' : ''}
            </p>
        </div>
    );
};

export default ClientContactsTab;
